import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dumbbell, Flame, Weight, History, Plus } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { userApi } from '@/services/api';
import { Card, Button } from '@/components/common';
import { WeightChart, MoodChart } from '@/components/dashboard';
import { formatAppLastUpdated, getTimeOfDayGreeting } from '@/config/appInfo';
import { useAppVersionInfo } from '@/hooks/useAppVersion';

interface UserStats {
  totalWorkouts: number;
  totalExercises: number;
  totalSets: number;
  totalReps: number;
  totalWeightLifted: number;
  averageDifficulty: number;
}

const Dashboard = () => {
  const { user } = useAuthStore();
  const versionInfo = useAppVersionInfo();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      if (!user) return;
      
      try {
        const response = await userApi.getStats(user.id);
        if (response.success && response.data) {
          setStats(response.data);
        }
      } catch (error) {
        console.error('Failed to load stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [user]);

  return (
    <div>
      {/* Verzió és frissítés dátuma */}
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
        <span>v{versionInfo?.version ?? '…'}</span>
        <span>
          Frissítve:{' '}
          {versionInfo ? formatAppLastUpdated(versionInfo.lastUpdated) : '…'}
        </span>
      </div>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {getTimeOfDayGreeting()}, {user?.name}!
        </h1>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        <Link to="/workout" className="block">
          <Button size="lg" icon={<Plus className="w-5 h-5" />} className="w-full">
            Új edzés
          </Button>
        </Link>
        <Link to="/history" className="block">
          <Button size="lg" variant="outline" icon={<History className="w-5 h-5" />} className="w-full">
            Előzmények
          </Button>
        </Link>
      </div>

      {/* Hangulat grafikon */}
      <div className="mb-8">
        <MoodChart />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Dumbbell className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {loading ? '...' : stats?.totalWorkouts ?? 0}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Edzés</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Flame className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {loading ? '...' : stats?.totalSets ?? 0}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Sorozat</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Weight className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {loading ? '...' : `${(stats?.totalWeightLifted ?? 0).toLocaleString()}`}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">kg emelt</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <History className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {loading ? '...' : stats?.totalReps ?? 0}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Ismétlés</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Súlyváltozás – legalul */}
      <WeightChart />
    </div>
  );
};

export default Dashboard;
